import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameConfig, ATTR_SCALE } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { noticeSystem } from './NoticeSystem';
import { scheduleSystem } from './ScheduleSystem';

/**
 * 咖位名气门槛的**本文件读数口**：唯一真源已搬到 `GameConfig.RANK_FAME_THRESHOLDS`
 * （2026-10-22 搬家，值不变；改门槛请改配置，别在这里再写一份）。
 * 表内注释（定标来历、尾三档改动、NPC 侧共用）随表一起在 GameConfig。
 */
const RANK_THRESHOLDS = GameConfig.RANK_FAME_THRESHOLDS;

/**
 * 咖位名称（策划定稿，女版）
 * 主角固定女性；男 NPC 用 RANK_NAMES_MALE（批次2拍板：仅 Lv.8/9 有性别差异）
 */
export const RANK_NAMES: string[] = [
    '无人在意',   // 1
    '稍有人知',   // 2
    '圈内眼熟',   // 3
    '小有名气',   // 4
    '三线艺人',   // 5（2026-10-31 咖位重排：三线插入，腰部加厚）
    '二线艺人',   // 6
    '一线艺人',   // 7
    '顶流艺人',   // 8
    '国民女神',   // 9（原 Lv.8；影坛天后已并入国际巨星）
    '国际巨星',   // 10
];

/**
 * 咖位名称（男版）：Lv.9 国民男神，其余与女版一致（原 Lv.8/9 双差异随重排收敛为 Lv.9 单差异）
 */
export const RANK_NAMES_MALE: string[] = [
    '无人在意',   // 1
    '稍有人知',   // 2
    '圈内眼熟',   // 3
    '小有名气',   // 4
    '三线艺人',   // 5
    '二线艺人',   // 6
    '一线艺人',   // 7
    '顶流艺人',   // 8
    '国民男神',   // 9
    '国际巨星',   // 10
];

/**
 * 晋升口碑门槛（仅晋升时判定，晋升后不复查）
 * 键 = 目标咖位等级（canPromote 以 newRank 直查，1-based）。
 * 2026-10-31 咖位重排：门槛随「名」走——国民女神（新 Lv.9）=300、国际巨星（Lv.10）=500；
 * 原 Lv.9 影坛的 400 门随「影坛并入国际」一并消失（三道门收敛为两道，顶流 Lv.8 回归纯名气晋升）。
 * 修正记档：原键 7/8/9 系 0-based 笔误，实际生效比设计稿提前一级（升 Lv.7 就要 60、Lv.10 无门槛），本批（批4·四修正）改回
 * 批3-A：门槛数值同乘尺度保持占比；尺度扩充批（2026-09-28 全案）：门槛随 ATTR_SCALE=5 抬到 300/400/500，
 * 而口碑获取仍按成长尺 ×3 ⇒ 高档冲榜相对收紧（用户知情拍板，实测口碑节奏如有问题再单改本表）
 * ⚠️ 2026-10-07 同日往返回记：口碑面定五档后用户一度令「国民女神改 100、天后改 300（对齐档位上沿）」，
 *   当日即撤回（「第三点改回来，国民女神还是300，天后400」）⇒ **国民门 300 ⛔ 再动**。
 *   留这条是因为黑红线路那批（替代晋升口＝或门）会来读本表：白线要是降到 100，黑线（口碑最低档）就成摆设，
 *   两条线现在各占一档两端（白＝T4 上沿往上、黑＝T1 区），⛔ 有人顺手"对齐"把白线拉低。
 */
const RANK_REPUTATION_GATES: Record<number, number> = {
    9: 60 * ATTR_SCALE,   // Lv.9 国民女神/国民男神
    10: 100 * ATTR_SCALE, // Lv.10 国际巨星
};

/**
 * 顶流之后的另外两道门（2026-10-22 咖位曲线批·用户拍板；2026-10-31 重排后=国民/国际两道）：
 * 名气在 Lv.8（顶流）之后退化为「入场券」（门槛只 +3000，一部半戏即穿），真正卡人的是
 * ① 口碑（上表）② 主演高分作品 ③ 最佳女主角奖——都只能靠演好戏拿，刷不出来。
 * 数值口径：
 *  - 作品：`filmography` 里 **一番/二番**（billing ≤ 2）且评分 ≥ RANK_BEST_WORK_MIN_SCORE 的条数（8.5）；
 *    旧存档的履历没有 billing 快照 ⇒ 回表 `GameData.SCRIPT_MAP` 反查（剧本下架时该条不计）。
 *  - 奖项：`awardHistory` 里 category = 'bestActress' 且 playerWon 的奖种数；
 *    Lv.10 = 金枢 + 星汉**双料**（各 ≥1 座），与 TAG_302 / TAG_306 同源（原 Lv.9 任一奖门随影坛并档退役）。
 * 定标依据：口碑 300/500 在满档线路落在 Y14/Y18 一带，作品 2/6 与奖项同期间可达（重排后读数待复测）。
 */
const RANK_WORK_GATES: Record<number, number> = { 9: 2, 10: 6 };
const RANK_AWARD_GATES: Record<number, 1 | 2> = { 10: 2 };

/**
     * 咖位系统
     * 管理名气 → 咖位等级映射，晋升（口碑门槛）/降级判定
     */
    export class RankSystem {
    private static instance: RankSystem;

    private constructor() { }

    static getInstance(): RankSystem {
        if (!this.instance) {
            this.instance = new RankSystem();
        }
        return this.instance;
    }

    /** 根据名气值获取当前咖位等级 */
    getRankFromFame(fame: number): number {
        for (let i = RANK_THRESHOLDS.length - 1; i >= 0; i--) {
            if (fame >= RANK_THRESHOLDS[i]) {
                return i + 1; // 1-based
            }
        }
        return 1;
    }

    /** 获取当前咖位名称（缺省女版=主角；男 NPC 传 gender='male'） */
    getRankName(rank: number, gender?: 'male' | 'female'): string {
        const idx = Math.max(0, Math.min(rank - 1, RANK_NAMES.length - 1));
        return (gender === 'male' ? RANK_NAMES_MALE : RANK_NAMES)[idx];
    }

    /** 获取升到下一级所需名气 */
    getFameToNextRank(rank: number): number {
        if (rank >= GameConfig.RANK_MAX) return -1; // 已满级
        const nextThreshold = RANK_THRESHOLDS[rank]; // rank 是 1-based，索引是 rank（因为第0个是Lv.1的门槛）
        const currentFame = GameState.getInstance().getFame();
        return Math.max(0, nextThreshold - currentFame);
    }

    /** 获取指定咖位等级的名气门槛（供 NPC 初始 fame 使用） */
    getRankThreshold(rank: number): number {
        const idx = Math.max(0, Math.min(rank - 1, RANK_THRESHOLDS.length - 1));
        return RANK_THRESHOLDS[idx];
    }

    /**
     * 「升到下一级」那道台阶的涨幅（本级门槛 → 下一级门槛），供营业收益封顶用。
     * 满级无下一级 ⇒ 沿用升进满级那一级（Lv.9→Lv.10），⛔ 放开成无限复利。
     * 按**当前咖位**而非名气反算的等级取档：口碑线卡住升咖时，名气收入也跟着卡在这道台阶上。
     */
    getPromotionStep(rank: number): number {
        const max = RANK_THRESHOLDS.length;
        const clamped = Math.max(1, Math.min(rank, max));
        const idx = clamped >= max ? max - 1 : clamped;
        return RANK_THRESHOLDS[idx] - RANK_THRESHOLDS[idx - 1];
    }

    /**
     * **咖位收入乘子**（2026-10-22 咖位曲线批；2026-10-23 H 案改读本级门槛）：
     *   √(max(Lv.2 门槛, 本级名气门槛) ÷ Lv.2 门槛)。
     * 用途＝作品名气收益（`ScriptSystem.fameGainOf`）＋ 营业/公益封顶（`FanSystem`，两处同源一把尺）。
     * 为什么是「门槛开方」而不是另立一张手调表（10-22 的理由今天仍成立）：
     *  - 门槛曲线是几何的（30→15000＝500 倍），咖位等级是线性的（1~10）⇒ 用等级当乘子追不上；
     *  - 开方 ⇒ 收入涨得比门槛慢一半 ⇒ 每级所需部数自然递增；
     *  - 单调 ⇒ 同一部戏同角色，高咖位收益必然 ≥ 低咖位（结构上不可能「顶流不如一线」）。
     * 【2026-10-23 为什么从「下一级门槛」改成「本级门槛」】门槛表尾段每级只 +3000
     *  ＝名气退化成入场券，读「下一级」的乘子却还在跟涨 ⇒ 单部进账远超台阶差，
     *  「一部戏穿一级」成为公式必然（用户实测第 6 年 3 万名气）。改读本级别 ⇒
     *  顶流（2026-10-31 重排后=Lv.8，门槛 18000）起乘子基本锁死（√(18000/30)≈24.5），与「顶流后看口碑/作品/奖项」的设计语义对齐。
     * floor `max(T[1], T[rank-1])` 统一式：Lv.1 门槛是 0，裸开方得 0.18 会让新人乘子塌到地板；
     *  取 Lv.2 门槛兜底 ⇒ Lv.1/Lv.2 自然都=1，⛔ 写等级分支（分支＝第二把尺）。
     */
    getRankFameMult(rank: number): number {
        const max = RANK_THRESHOLDS.length;
        const clamped = Math.max(1, Math.min(rank, max));
        const base = Math.max(1, RANK_THRESHOLDS[1] ?? 1);
        const cur = RANK_THRESHOLDS[clamped - 1] ?? RANK_THRESHOLDS[max - 1];
        return Math.sqrt(Math.max(base, cur) / base);
    }

    /**
     * **顶流线**= 顶流档名气门槛，由门槛表现算（2026-10-31 重排后= Lv.8 档）。
     * 用途＝**名气因子的钳点**（「顶流之后看口碑/作品/奖项」的复利闸门，涨得动但追不上）。
     * 2026-10-23 H 案：原常量 `FAME_FAME_FACTOR_REF: 24000`（写死满级线）退役——它是「全库同步点清单」
     * 第 4 类病根（门槛表一改，写死的读数就过期）。
     * ⚠️ **软上限不读这里**（它锚满级线，见 `getMaxFameThreshold()`）——两条锚语义不同，混用会造出尾段倒挂。
     */
    getToplineFameRef(): number {
        return this.getRankThreshold(8);   // 顶流档（2026-10-31 重排后=Lv.8）
    }

    /**
     * **满级线**= Lv.10「国际巨星」名气门槛，由门槛表现算。
     * 用途＝**名气软上限的起跳点**（纯时间刹车：名气越过满级线后收益按 (满级线÷名气)^指数 衰减，
     * 防名气膨胀到亿级粉丝——用户那份 3 万档正落这段，刹车要管得住）。
     * ⚠️ 与 `getToplineFameRef()` 语义不同，⛔ 混用：软上限若锚顶流线，玩家一过 15000 就衰减，
     * 衰减盖过乘子微涨 ⇒ 同一部戏 Lv.8 < Lv.7 倒挂（用户裁定「尽量避免倒挂」）。
     */
    getMaxFameThreshold(): number {
        return this.getRankThreshold(GameConfig.RANK_MAX);
    }

    /**
     * 本级「收入台阶」＝ FAME_INCOME_BASIS_BASE × 咖位乘子（单调不降）。
     * 营业帖/公益的名气封顶读它，⛔ 读 `getPromotionStep()`：门槛尾段是入场券（只 +3000），
     * 读晋升台阶会让顶流的发帖收益掉到一线的三分之一（与作品收益倒挂同源，2026-10-22 修）。
     */
    getIncomeBasis(rank: number): number {
        return GameConfig.FAME_INCOME_BASIS_BASE * this.getRankFameMult(rank);
    }

    /** 主演高分作品数：一番/二番（billing ≤ RANK_BEST_WORK_MAX_BILLING）且评分 ≥ RANK_BEST_WORK_MIN_SCORE */
    getBestWorkCount(): number {
        const p = GameState.getInstance().player;
        let n = 0;
        for (const f of p.filmography ?? []) {
            if (f.score < GameConfig.RANK_BEST_WORK_MIN_SCORE) continue;
            const billing = f.billing
                ?? GameData.SCRIPT_MAP[f.scriptId]?.roles[f.roleIndex]?.billing;
            if (billing !== undefined && billing <= GameConfig.RANK_BEST_WORK_MAX_BILLING) n++;
        }
        return n;
    }

    /** 最佳女主角获奖档：0 = 无，1 = 任一奖 1 座，2 = 金枢＋星汉双料（各 ≥1 座） */
    getBestActressAwardTier(): 0 | 1 | 2 {
        const history = GameState.getInstance().player.awardHistory ?? [];
        const won = new Set(history.filter(r => r.playerWon && r.category === 'bestActress').map(r => r.awardId));
        if (won.size === 0) return 0;
        return won.has('jingshu') && won.has('xinghan') ? 2 : 1;
    }

    /** 获取咖位进度（0-1），供 UI 使用 */
    getRankProgress(rank: number, fame: number): number {
        if (rank >= GameConfig.RANK_MAX) return 1;
        const currentThreshold = RANK_THRESHOLDS[rank - 1];
        const nextThreshold = RANK_THRESHOLDS[rank];
        const range = nextThreshold - currentThreshold;
        if (range <= 0) return 1;
        const progress = (fame - currentThreshold) / range;
        return Math.max(0, Math.min(1, progress));
    }

    /**
     * 尝试晋升到目标咖位，返回是否晋升
     * Lv.9/10 需口碑 ≥300/500（显示值；2026-10-31 重排后两道门，顶流 Lv.8 纯名气；晋升时判定，晋升后不复查）
     */
    canRankUp(rank: number): boolean {
        return this.canPromote(rank);
    }

    /**
     * 检查咖位晋升（月末等聚合时点调用），返回是否有晋升。
     * ⚠️ 逐级台阶（2026-10-03 修「名气1万+口碑420 却纹丝不动」）：旧算法一步跳到名气够得着的
     * 最高级、只查那一级口碑门槛 ⇒ 名气越过满级线后目标直接=Lv.10（需口碑 500），中间
     * 各级口碑门被整体跳过、400+ 口碑两头够不着 ⇒ 整卡。现从 rank+1 逐级判：
     * 够门槛就升一级继续，卡在哪级门槛就停在哪级并报哪级的数（阶梯弹窗口径一致）。
     */
    checkRankUp(): boolean {
        const state = GameState.getInstance();
        const fameRank = this.getRankFromFame(state.player.fame);
        let promoted = false;
        while (state.player.rank < fameRank && state.player.rank < GameConfig.RANK_MAX) {
            const next = state.player.rank + 1;
            if (!this.canPromote(next)) {
                const miss = this.getGateMissHint(next);
                break;
            }
            const oldRank = state.player.rank;
            state.player.rank = next;
            console.log(`[RankSystem] 咖位晋升！${oldRank} → ${next}（${this.getRankName(next)}）`);
            EventBus.emit(GameEvents.RANK_CHANGED, next, oldRank);
            // 经纪人月度通知（页面 5b 聊天页历史通知）
            // 咖位口径（2026-10-23 UI规范 §6 维护规则）：玩家可见读数一律用咖位名，
            // `Lv.` 只许进控制台/测试/代码注释——与咖位阶梯弹窗「不列等级列」同一把尺。
            noticeSystem.push('rankUp',
                // 台词表 #5（~豁免昵称）
                `成功晋升「${this.getRankName(next)}」了。`);
            promoted = true;
        }
        if (promoted) {
            // 咖位晋升可能解锁新工作（咖位 + 训练次数双门槛）：终级一次扫，覆盖连跳穿过的各级
            scheduleSystem.notifyWorkUnlocksIfNew();
            // 刷新剧本池：新等级剧本可能解锁（剧本等级=咖位要求，rank 变高后更多剧本可接）
            state.refreshMarket();
        }
        return promoted;
    }

    /**
     * 晋升门判定（逐条 AND）：口碑 → 主演高分作品 → 最佳女主角奖。
     * Lv.1~7 无门（纯名气）；Lv.8/9/10 三道门（数值见 RANK_REPUTATION_GATES / RANK_WORK_GATES / RANK_AWARD_GATES）。
     */
    private canPromote(rank: number): boolean {
        const repGate = RANK_REPUTATION_GATES[rank];
        if (repGate !== undefined && GameState.getInstance().getAttribute('reputation') < repGate) return false;
        const workGate = RANK_WORK_GATES[rank];
        if (workGate !== undefined && this.getBestWorkCount() < workGate) return false;
        const awardGate = RANK_AWARD_GATES[rank];
        if (awardGate !== undefined && this.getBestActressAwardTier() < awardGate) return false;
        return true;
    }

    /** 卡在哪道门（给日志/阶梯弹窗共用的一句话，按 口碑 → 作品 → 奖项 顺序报第一道没过的） */
    getGateMissHint(rank: number): string {
        const state = GameState.getInstance();
        const repGate = RANK_REPUTATION_GATES[rank];
        if (repGate !== undefined && state.getAttribute('reputation') < repGate) {
            return `口碑不足（需 ${repGate}）`;
        }
        const workGate = RANK_WORK_GATES[rank];
        if (workGate !== undefined && this.getBestWorkCount() < workGate) {
            return `主演高分作品不足（需 ${workGate} 部评分 ≥${GameConfig.RANK_BEST_WORK_MIN_SCORE} 的一/二番）`;
        }
        const awardGate = RANK_AWARD_GATES[rank];
        if (awardGate !== undefined && this.getBestActressAwardTier() < awardGate) {
            return awardGate === 2
                ? '还未集齐金枢＋星汉双料影后'
                : '还缺一座最佳女主角';
        }
        return '实绩不足';
    }

    /** 强制设置咖位（用于开局/调试/商店购买开局咖位） */
    setRank(rank: number): void {
        const state = GameState.getInstance();
        const clamped = Math.max(1, Math.min(rank, GameConfig.RANK_MAX));
        const oldRank = state.player.rank;
        state.player.rank = clamped;
        // 同步设置名气到该等级门槛
        if (clamped > 1) {
            const minFame = RANK_THRESHOLDS[clamped - 1] ?? 0;
            if (state.player.fame < minFame) {
                state.player.fame = minFame;
                // 剧宣批：开局咖位=名气抬到门槛，粉丝盘**一步给足整锚**（买的是既有地位，⛔ 只按涨粉那条 50% 即时比例给）
                state.player.fans = Math.max(state.player.fans, state.getFanAnchor());
            }
        }
        // 咖位变化后刷新剧本池：新等级剧本可能解锁（剧本等级=咖位要求）
        state.refreshMarket();
        EventBus.emit(GameEvents.RANK_CHANGED, clamped, oldRank);
    }

    /** 获取全部咖位阈值（供 UI 进度条使用） */
    getThresholds(): number[] {
        return [...RANK_THRESHOLDS];
    }

    /**
     * 该等级的晋升口碑门槛（供「咖位阶梯」弹窗读，数值只活在本表 RANK_REPUTATION_GATES 一处）。
     * 返回 null = 该级⛔ 口碑门槛（Lv.1~7 只卡名气 ⇒ UI 那格显示「—」而⛔ 编一个数）。
     */
    getReputationGate(rank: number): number | null {
        return RANK_REPUTATION_GATES[rank] ?? null;
    }

    /** 该级的「主演高分作品」门槛（供咖位阶梯弹窗读；null = 无要求） */
    getWorkGate(rank: number): number | null {
        return RANK_WORK_GATES[rank] ?? null;
    }

    /** 该级的「最佳女主角奖」门槛：1 = 任一奖 1 座，2 = 双料（null = 无要求） */
    getAwardGate(rank: number): 1 | 2 | null {
        return RANK_AWARD_GATES[rank] ?? null;
    }

    /** 获取咖位名称列表（默认女版；男 NPC 传 'male'） */
    getRankNames(gender?: 'male' | 'female'): string[] {
        return [...(gender === 'male' ? RANK_NAMES_MALE : RANK_NAMES)];
    }
}

/** 导出单例 */
export const rankSystem = RankSystem.getInstance();